import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useToast } from "@/lib/toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SettingsTable, type ColumnDef } from "@/components/settings/SettingsTable";
import { cn } from "@/lib/cn";

const SECTIONS = [
  "persona",
  "behavior",
  "embeddings",
  "model-routing",
  "lead-scoring",
  "human-takeover",
  "followup",
  "qualification",
  "channel-auto-reply",
] as const;
type Section = typeof SECTIONS[number];

const LABELS: Record<Section, string> = {
  "persona": "Persona & instructions",
  "behavior": "Behavior",
  "embeddings": "Embeddings (RAG)",
  "model-routing": "Model routing",
  "lead-scoring": "Lead scoring",
  "human-takeover": "Human takeover",
  "followup": "Follow-up",
  "qualification": "Qualification fields",
  "channel-auto-reply": "Auto-reply per channel",
};

const EMBEDDING_MODELS = [
  { value: "openai/text-embedding-3-small", label: "OpenAI text-embedding-3-small (default, 1536d, $0.02/M)" },
  { value: "openai/text-embedding-3-large", label: "OpenAI text-embedding-3-large (truncated to 1536d, $0.13/M)" },
  { value: "openai/text-embedding-ada-002", label: "OpenAI text-embedding-ada-002 (legacy, 1536d, $0.10/M)" },
];

export function AIBotPage() {
  const [section, setSection] = useState<Section>("persona");

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">AI Bot</h1>
        <p className="text-sm text-slate-500">
          How the bot speaks, how it decides, when it escalates, what it extracts. The business context lives in <strong>Business</strong>; this is the HOW.
        </p>
      </header>

      <div className="flex gap-1 border-b border-slate-200 -mb-px overflow-x-auto">
        {SECTIONS.map((s) => (
          <button
            key={s}
            onClick={() => setSection(s)}
            className={cn(
              "whitespace-nowrap px-3 py-2 text-sm border-b-2",
              section === s
                ? "border-slate-900 text-slate-900 font-medium"
                : "border-transparent text-slate-500 hover:text-slate-900",
            )}
          >
            {LABELS[s]}
          </button>
        ))}
      </div>

      <div className="pt-2">
        {section === "persona" && <PersonaPanel />}
        {section === "behavior" && <BehaviorPanel />}
        {section === "embeddings" && <EmbeddingsPanel />}
        {section === "model-routing" && <ModelRoutingPanel />}
        {section === "lead-scoring" && <LeadScoringPanel />}
        {section === "human-takeover" && <HumanTakeoverPanel />}
        {section === "followup" && <FollowupPanel />}
        {section === "qualification" && <QualificationFieldsPanel />}
        {section === "channel-auto-reply" && <ChannelAutoReplyPanel />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Persona panel - voice, length, emoji, addressing, do/don't
// ---------------------------------------------------------------------------

function PersonaPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["settings", "/api/settings/business"],
    queryFn: () => api.get<Record<string, unknown>>("/api/settings/business"),
  });
  const toast = useToast();
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const saveMut = useMutation({
    mutationFn: () => api.put("/api/settings/business", draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings", "/api/settings/business"] });
      const n = Object.keys(draft).length;
      setDraft({});
      toast.success("Persona saved", `${n} field${n > 1 ? "s" : ""} updated`);
    },
    onError: (e) => toast.error("Save failed", e instanceof Error ? e.message : String(e)),
  });
  const get = (k: string) => (draft[k] !== undefined ? draft[k] : data?.[k]);
  const set = (k: string, v: unknown) => setDraft({ ...draft, [k]: v });
  const dirty = Object.keys(draft).length > 0;

  if (!data) return <Card><CardContent className="py-6 text-sm text-slate-500">Loading…</CardContent></Card>;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Voice & style</CardTitle>
          <CardDescription>How the bot sounds. These are baked into every system prompt.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Field label="Tone of voice (free-form, ≤120 chars)">
            <Input
              placeholder="ex: warm, professional, slightly playful"
              maxLength={120}
              value={String(get("bot_tone_of_voice") ?? "")}
              onChange={(e) => set("bot_tone_of_voice", e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Response length">
              <SelectInput
                value={String(get("bot_response_length") ?? "")}
                onChange={(v) => set("bot_response_length", v)}
                options={[
                  { value: "", label: "- default -" },
                  { value: "short", label: "Short" },
                  { value: "medium", label: "Medium" },
                  { value: "long", label: "Long" },
                ]}
              />
            </Field>
            <Field label="Emoji policy">
              <SelectInput
                value={String(get("bot_emoji_policy") ?? "")}
                onChange={(v) => set("bot_emoji_policy", v)}
                options={[
                  { value: "", label: "- default -" },
                  { value: "never", label: "Never" },
                  { value: "occasional", label: "Occasional" },
                  { value: "frequent", label: "Frequent" },
                ]}
              />
            </Field>
            <Field label="Addressing">
              <SelectInput
                value={String(get("bot_addressing") ?? "")}
                onChange={(v) => set("bot_addressing", v)}
                options={[
                  { value: "", label: "- default -" },
                  { value: "formal", label: "Formal (vous)" },
                  { value: "casual", label: "Casual (tu)" },
                  { value: "match_user", label: "Match the prospect" },
                ]}
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Special instructions</CardTitle>
          <CardDescription>
            Granular do's & don'ts. These end up at the end of the system prompt and override generic defaults.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Field label="Things you SHOULD do (≤4000 chars)">
            <Textarea
              rows={4}
              placeholder="ex: Always confirm the area and budget before suggesting a villa. Always mention that a real visit can be scheduled within 48h."
              value={String(get("bot_special_do") ?? "")}
              onChange={(v) => set("bot_special_do", v)}
            />
          </Field>
          <Field label="Things you must NEVER do (≤4000 chars)">
            <Textarea
              rows={4}
              placeholder="ex: Never promise a discount without checking with the human team. Never share the owner's phone number."
              value={String(get("bot_special_dont") ?? "")}
              onChange={(v) => set("bot_special_dont", v)}
            />
          </Field>
          <Field label="Always mention when relevant (one-liner, ≤500)">
            <Input
              maxLength={500}
              placeholder="ex: Our cleaning service is included in the price"
              value={String(get("bot_always_mention") ?? "")}
              onChange={(e) => set("bot_always_mention", e.target.value)}
            />
          </Field>
          <Field label="Always avoid mentioning (one-liner, ≤500)">
            <Input
              maxLength={500}
              placeholder="ex: do not mention competitors by name"
              value={String(get("bot_always_avoid") ?? "")}
              onChange={(e) => set("bot_always_avoid", e.target.value)}
            />
          </Field>
        </CardContent>
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <div className="rounded-md bg-brand-surface shadow-md border border-slate-200 px-3 py-2 flex items-center gap-3">
          <span className="text-xs text-slate-500">
            {dirty ? `${Object.keys(draft).length} unsaved change(s)` : "All saved"}
          </span>
          <Button onClick={() => saveMut.mutate()} disabled={!dirty || saveMut.isPending}>
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Behavior - temperature, max_tokens, thresholds, vertical addendum
// ---------------------------------------------------------------------------

function BehaviorPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({
    queryKey: ["settings", "/api/settings/ai-behavior"],
    queryFn: () => api.get<Record<string, unknown>>("/api/settings/ai-behavior"),
  });
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const saveMut = useMutation({
    mutationFn: () => api.put("/api/settings/ai-behavior", draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings", "/api/settings/ai-behavior"] });
      setDraft({});
      toast.success("AI behavior saved");
    },
    onError: (e) => toast.error("Save failed", e instanceof Error ? e.message : String(e)),
  });
  const get = (k: string) => (draft[k] !== undefined ? draft[k] : data?.[k]);
  const set = (k: string, v: unknown) => setDraft({ ...draft, [k]: v });

  if (!data) return <Card><CardContent className="py-6 text-sm text-slate-500">Loading…</CardContent></Card>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Behavior</CardTitle>
        <CardDescription>
          Default model + sampling. Per-task overrides live in <em>Model routing</em>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Default model">
            <Input value={String(get("model") ?? "")} onChange={(e) => set("model", e.target.value)} />
          </Field>
          <Field label="Reply temperature [0-2]">
            <Input type="number" step="0.05" min="0" max="2"
              value={String(get("reply_temperature") ?? "")}
              onChange={(e) => set("reply_temperature", Number(e.target.value))} />
          </Field>
          <Field label="Extraction temperature [0-2]">
            <Input type="number" step="0.05" min="0" max="2"
              value={String(get("extraction_temperature") ?? "")}
              onChange={(e) => set("extraction_temperature", Number(e.target.value))} />
          </Field>
          <Field label="Max tokens">
            <Input type="number" min="1" max="8192"
              value={String(get("max_tokens") ?? "")}
              onChange={(e) => set("max_tokens", Number(e.target.value))} />
          </Field>
          <Field label="Hot lead score threshold [0-100]">
            <Input type="number" min="0" max="100"
              value={String(get("hot_lead_score") ?? "")}
              onChange={(e) => set("hot_lead_score", Number(e.target.value))} />
          </Field>
          <Field label="Escalation score threshold [0-100]">
            <Input type="number" min="0" max="100"
              value={String(get("escalation_score") ?? "")}
              onChange={(e) => set("escalation_score", Number(e.target.value))} />
          </Field>
        </div>
        <Field label="Vertical addendum (≤500 chars, appended last in the system prompt - kept for legacy compat)">
          <Textarea
            rows={3}
            value={String(get("vertical_addendum") ?? "")}
            onChange={(v) => set("vertical_addendum", v)}
          />
        </Field>
        <div className="flex justify-end">
          <Button onClick={() => saveMut.mutate()} disabled={Object.keys(draft).length === 0 || saveMut.isPending}>
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tables (model routing, lead scoring, takeover, followup, qualif, channels)
// ---------------------------------------------------------------------------

function ModelRoutingPanel() {
  const cols: ColumnDef[] = [
    { key: "task", label: "Task", type: "readonly", render: (v) => <code className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{String(v)}</code> },
    { key: "model", label: "Model", type: "text" },
    { key: "temperature", label: "Temp", type: "number" },
    { key: "max_tokens", label: "Max tokens", type: "number" },
    { key: "is_active", label: "Active", type: "bool" },
    { key: "notes", label: "Notes", type: "text" },
  ];
  return (
    <SettingsTable
      title="Model routing"
      description="One row per AI task. Active rows override the default model. See openrouter.ai/models."
      endpoint="/api/settings/ai-model-routing"
      columns={cols}
    />
  );
}

function LeadScoringPanel() {
  const cols: ColumnDef[] = [
    { key: "signal", label: "Signal", type: "readonly", render: (v) => <code className="font-mono text-xs">{String(v)}</code> },
    { key: "delta", label: "Delta", type: "number" },
    { key: "priority", label: "Priority", type: "number" },
    { key: "is_active", label: "Active", type: "bool" },
  ];
  return (
    <SettingsTable
      title="Lead scoring rules"
      description="Each signal applies a delta to the contact's score (clamped 0-100)."
      endpoint="/api/settings/lead-scoring-rules"
      columns={cols}
    />
  );
}

function HumanTakeoverPanel() {
  const cols: ColumnDef[] = [
    { key: "match_kind", label: "Match kind", type: "readonly" },
    { key: "match_value", label: "Value", type: "text" },
    { key: "notification_type", label: "Notification type", type: "text" },
    { key: "priority", label: "Priority", type: "number" },
    { key: "is_active", label: "Active", type: "bool" },
  ];
  return (
    <SettingsTable
      title="Human takeover rules"
      description="When a rule matches the AI output, the conversation flips into human takeover (AI paused, draft saved, notification fired)."
      endpoint="/api/settings/human-takeover-rules"
      columns={cols}
    />
  );
}

function FollowupPanel() {
  const cols: ColumnDef[] = [
    { key: "type", label: "Type", type: "readonly" },
    { key: "delay_hours", label: "Delay (h)", type: "number" },
    { key: "defer_when_takeover", label: "When takeover", type: "select:cancel|defer_24h|defer_72h" },
    { key: "channel_strategy", label: "Channel", type: "select:same_as_conversation|primary_phone|best_available" },
    { key: "message_category", label: "Message category", type: "text" },
    { key: "is_active", label: "Active", type: "bool" },
  ];
  return (
    <SettingsTable
      title="Follow-up rules"
      description="The cron scans followup_jobs every 15 min. Inactive types are skipped."
      endpoint="/api/settings/followup-rules"
      columns={cols}
    />
  );
}

function QualificationFieldsPanel() {
  const cols: ColumnDef[] = [
    { key: "field_key", label: "Key", type: "readonly", render: (v) => <code className="font-mono text-xs">{String(v)}</code> },
    { key: "label", label: "Label", type: "text" },
    { key: "data_type", label: "Type", type: "readonly" },
    { key: "is_required", label: "Required", type: "bool" },
    { key: "is_active", label: "Active", type: "bool" },
    { key: "priority", label: "Priority", type: "number" },
  ];
  return (
    <SettingsTable
      title="Qualification fields"
      description="Fields the AI extracts and asks for. The contact promotes to 'qualified' once all required fields are filled."
      endpoint="/api/settings/qualification-fields"
      columns={cols}
    />
  );
}

function ChannelAutoReplyPanel() {
  const cols: ColumnDef[] = [
    { key: "channel", label: "Channel", type: "readonly" },
    { key: "enabled", label: "AI auto-reply enabled", type: "bool" },
  ];
  return (
    <SettingsTable
      title="Auto-reply per channel"
      description="Disable to mute the AI on a specific channel (inbound still saved + requires_human_followup flipped)."
      endpoint="/api/settings/channel-auto-reply-settings"
      columns={cols}
    />
  );
}

// ---------------------------------------------------------------------------
// Embeddings (RAG) - model selector + reindex action
// ---------------------------------------------------------------------------

function EmbeddingsPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({
    queryKey: ["settings", "/api/settings/business"],
    queryFn: () => api.get<Record<string, unknown>>("/api/settings/business"),
  });
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const saveMut = useMutation({
    mutationFn: () => api.put("/api/settings/business", draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings", "/api/settings/business"] });
      setDraft({});
      toast.success("Embedding model saved", "New docs will use this model. Run 'Reindex all' to re-embed existing docs.");
    },
    onError: (e) => toast.error("Save failed", e instanceof Error ? e.message : String(e)),
  });
  const get = (k: string) => (draft[k] !== undefined ? draft[k] : data?.[k]);
  const set = (k: string, v: unknown) => setDraft({ ...draft, [k]: v });

  // Reindex-all = list active docs + POST /api/knowledge/:id/reindex for each.
  const [reindexing, setReindexing] = useState(false);
  const [reindexProgress, setReindexProgress] = useState({ done: 0, total: 0, failed: 0 });

  async function reindexAll() {
    if (!confirm("Re-embed all active knowledge documents with the current model? This can take 30-60s for a typical KB.")) return;
    setReindexing(true);
    setReindexProgress({ done: 0, total: 0, failed: 0 });
    try {
      const docs = await api.get<{ id: string }[]>("/api/knowledge?limit=500&is_active=true");
      setReindexProgress({ done: 0, total: docs.length, failed: 0 });
      let failed = 0;
      for (let i = 0; i < docs.length; i++) {
        try {
          await api.post(`/api/knowledge/${docs[i]!.id}/reindex`, {});
        } catch {
          failed++;
        }
        setReindexProgress({ done: i + 1, total: docs.length, failed });
      }
      if (failed === 0) toast.success(`${docs.length} docs reindexed`, "All knowledge is now vector-indexed.");
      else toast.warn(`Reindex done with ${failed} failure(s)`, `${docs.length - failed} ok / ${failed} failed`);
    } catch (e) {
      toast.error("Reindex failed", e instanceof Error ? e.message : String(e));
    } finally {
      setReindexing(false);
    }
  }

  if (!data) return <Card><CardContent className="py-6 text-sm text-slate-500">Loading…</CardContent></Card>;

  const currentModel = String(get("embedding_model") ?? "openai/text-embedding-3-small");
  const dirty = Object.keys(draft).length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Embeddings (RAG)</CardTitle>
        <CardDescription>
          Which model converts your knowledge documents into vectors for semantic search. All supported models output 1536-dim vectors so you can switch without re-migrating.
          Every save / edit of a knowledge doc auto-reindexes in the background.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field label="Embedding model (via OpenRouter)">
          <select
            value={currentModel}
            onChange={(e) => set("embedding_model", e.target.value)}
            className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm bg-brand-surface"
          >
            {EMBEDDING_MODELS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </Field>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 space-y-1.5">
          <div className="font-medium text-slate-900">How it works</div>
          <ul className="ml-5 list-disc space-y-0.5">
            <li>On <strong>save</strong> of a knowledge doc → auto-reindex in the background (1-3 s)</li>
            <li>On <strong>scrape</strong> of a website → each section is indexed once inserted</li>
            <li>On every <strong>inbound message</strong> → the query is embedded and matched against your KB by cosine similarity, fallback to keyword search if embedding fails</li>
            <li>Switching the model here = future docs use it. Existing docs keep their current vectors until you click <strong>Reindex all</strong></li>
          </ul>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={reindexAll}
              disabled={reindexing}
            >
              {reindexing ? `Reindexing… ${reindexProgress.done}/${reindexProgress.total}` : "Reindex all docs"}
            </Button>
            {reindexProgress.failed > 0 && (
              <span className="text-[11px] text-red-700">{reindexProgress.failed} failed</span>
            )}
          </div>
          <Button onClick={() => saveMut.mutate()} disabled={!dirty || saveMut.isPending}>
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function Textarea({ rows, placeholder, value, onChange }: { rows: number; placeholder?: string; value: string; onChange: (v: string) => void }) {
  return (
    <textarea
      rows={rows}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
    />
  );
}

function SelectInput({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm bg-brand-surface"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}
